/**
 * excelIO.js
 * Excel import/export module using SheetJS (xlsx library)
 * Handles XLSX and CSV data operations
 */

// XLSX library reference - loaded dynamically when needed
let XLSX = null;

function getXLSX() {
  if (XLSX) return XLSX;
  try {
    // eslint-disable-next-line no-undef
    XLSX = typeof window !== 'undefined' && window.XLSX ? window.XLSX : null;
  } catch (e) {
    XLSX = null;
  }
  return XLSX;
}

/**
 * Column definitions for entity types
 * Maps entity types to their expected column names and field mappings
 */
const COLUMN_DEFINITIONS = {
  locations: [
    { key: 'id', label: 'ID', type: 'text' },
    { key: 'description', label: 'Description', type: 'text' },
    { key: 'parent', label: 'Parent', type: 'text' },
    { key: 'hierarchyLevel', label: 'Level', type: 'number' },
    { key: 'operationalRule', label: 'Operational Rule', type: 'text' },
    { key: 'phaseAssignment', label: 'Phase', type: 'text' },
  ],
  equipments: [
    { key: 'id', label: 'ID', type: 'text' },
    { key: 'description', label: 'Description', type: 'text' },
    { key: 'type', label: 'Type', type: 'text' },
    { key: 'location', label: 'Location', type: 'text' },
    { key: 'redundancyType', label: 'Redundancy', type: 'text' },
    { key: 'maxCapacity', label: 'Capacity', type: 'number' },
    { key: 'state', label: 'State', type: 'text' },
  ],
  failuremodels: [
    { key: 'id', label: 'ID', type: 'text' },
    { key: 'description', label: 'Description', type: 'text' },
    { key: 'distribution', label: 'Distribution', type: 'text' },
    { key: 'eta', label: 'Eta', type: 'number' },
    { key: 'beta', label: 'Beta', type: 'number' },
    { key: 'mttf', label: 'MTTF', type: 'number' },
    { key: 'mttr', label: 'MTTR', type: 'number' },
  ],
  consequences: [
    { key: 'id', label: 'ID', type: 'text' },
    { key: 'description', label: 'Description', type: 'text' },
    { key: 'costRate', label: 'Cost Rate', type: 'number' },
    { key: 'costPerOccurrence', label: 'Cost Per Occurrence', type: 'number' },
    { key: 'capacityLoss', label: 'Capacity Loss', type: 'number' },
    { key: 'safetyCrit', label: 'Safety Critical', type: 'boolean' },
    { key: 'opCrit', label: 'Operational Critical', type: 'boolean' },
  ],
  spares: [
    { key: 'id', label: 'ID', type: 'text' },
    { key: 'description', label: 'Description', type: 'text' },
    { key: 'stockLevel', label: 'Stock Level', type: 'number' },
    { key: 'reorderPoint', label: 'Reorder Point', type: 'number' },
    { key: 'unitCost', label: 'Unit Cost', type: 'number' },
    { key: 'leadTime', label: 'Lead Time', type: 'number' },
    { key: 'poolType', label: 'Pool Type', type: 'text' },
  ],
  labor: [
    { key: 'id', label: 'ID', type: 'text' },
    { key: 'description', label: 'Description', type: 'text' },
    { key: 'costRate', label: 'Cost Rate', type: 'number' },
    { key: 'available', label: 'Available', type: 'number' },
    { key: 'shift', label: 'Shift', type: 'text' },
    { key: 'craftSkill', label: 'Craft Skill', type: 'text' },
    { key: 'crewSize', label: 'Crew Size', type: 'number' },
  ],
  scheduledtasks: [
    { key: 'id', label: 'ID', type: 'text' },
    { key: 'description', label: 'Description', type: 'text' },
    { key: 'interval', label: 'Interval', type: 'number' },
    { key: 'duration', label: 'Duration', type: 'number' },
    { key: 'cost', label: 'Cost', type: 'number' },
    { key: 'taskType', label: 'Task Type', type: 'text' },
    { key: 'asGoodAsNew', label: 'As Good As New', type: 'boolean' },
  ],
  correctivetasks: [
    { key: 'id', label: 'ID', type: 'text' },
    { key: 'description', label: 'Description', type: 'text' },
    { key: 'failureModel', label: 'Failure Mode', type: 'text' },
    { key: 'repairDuration', label: 'Repair Duration', type: 'number' },
    { key: 'repairDistribution', label: 'Repair Distribution', type: 'text' },
    { key: 'laborReq', label: 'Labor Required', type: 'text' },
    { key: 'spareReq', label: 'Spare Required', type: 'text' },
    { key: 'repairCost', label: 'Repair Cost', type: 'number' },
    { key: 'holdForRepair', label: 'Hold For Repair', type: 'boolean' },
    { key: 'logisticDelay', label: 'Logistic Delay', type: 'number' },
  ],
  rbdblocks: [
    { key: 'id', label: 'ID', type: 'text' },
    { key: 'description', label: 'Description', type: 'text' },
    { key: 'blockType', label: 'Block Type', type: 'text' },
    { key: 'failureModel', label: 'Failure Mode', type: 'text' },
    { key: 'state', label: 'State', type: 'text' },
  ],
  rcmcauses: [
    { key: 'id', label: 'ID', type: 'text' },
    { key: 'description', label: 'Description', type: 'text' },
    { key: 'failureMode', label: 'Failure Mode', type: 'text' },
    { key: 'severity', label: 'Severity', type: 'number' },
    { key: 'rpn', label: 'RPN', type: 'number' },
  ],
  weibullsets: [
    { key: 'id', label: 'ID', type: 'text' },
    { key: 'description', label: 'Description', type: 'text' },
    { key: 'failureData', label: 'Failure Data', type: 'number' },
    { key: 'beta', label: 'Beta', type: 'number' },
    { key: 'eta', label: 'Eta', type: 'number' },
    { key: 'rSquared', label: 'R Squared', type: 'number' },
  ],
  boms: [
    { key: 'id', label: 'BOM ID', type: 'text' },
    { key: 'name', label: 'BOM Name', type: 'text' },
    { key: 'equipmentId', label: 'Equipment ID', type: 'text' },
    { key: 'description', label: 'Description', type: 'text' },
    { key: 'revision', label: 'Revision', type: 'text' },
    { key: 'status', label: 'Status', type: 'text' },
  ],
  bomParts: [
    { key: 'id', label: 'Part ID', type: 'text' },
    { key: 'bomId', label: 'BOM ID', type: 'text' },
    { key: 'partNumber', label: 'Part Number', type: 'text' },
    { key: 'description', label: 'Part Description', type: 'text' },
    { key: 'quantity', label: 'Quantity', type: 'number' },
    { key: 'unitCost', label: 'Unit Cost', type: 'number' },
    { key: 'leadTime', label: 'Lead Time (hrs)', type: 'number' },
    { key: 'supplier', label: 'Supplier', type: 'text' },
    { key: 'criticalFlag', label: 'Critical', type: 'boolean' },
  ],
  taskGroups: [
    { key: 'id', label: 'Group ID', type: 'text' },
    { key: 'name', label: 'Group Name', type: 'text' },
    { key: 'description', label: 'Description', type: 'text' },
    { key: 'taskIds', label: 'Tasks', type: 'text' },
    { key: 'interval', label: 'Group Interval', type: 'number' },
    { key: 'priority', label: 'Priority', type: 'text' },
    { key: 'resourceRequirements', label: 'Resources', type: 'text' },
  ],
};

export class ExcelIO {
  // ========================================================================
  // IMPORT METHODS
  // ========================================================================

  /**
   * Import a workbook from buffer
   * @param {ArrayBuffer} fileBuffer
   * @returns {Object} {sheets: [{name, data: [[cell...]]}]}
   */
  static importWorkbook(fileBuffer) {
    if (!XLSX) {
      throw new Error(
        'SheetJS (xlsx) library not installed. Run: npm install xlsx'
      );
    }

    try {
      const workbook = XLSX.read(fileBuffer, { type: 'array' });
      const sheets = [];

      for (const sheetName of workbook.SheetNames) {
        const worksheet = workbook.Sheets[sheetName];
        const data = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
        sheets.push({ name: sheetName, data });
      }

      return { sheets };
    } catch (error) {
      throw new Error(`Failed to import workbook: ${error.message}`);
    }
  }

  /**
   * Import entity data from Excel buffer with auto-column mapping
   * @param {ArrayBuffer} fileBuffer
   * @param {string} entityType
   * @returns {Object} {entities, unmappedColumns, warnings}
   */
  static importEntityData(fileBuffer, entityType) {
    if (!XLSX) {
      throw new Error(
        'SheetJS (xlsx) library not installed. Run: npm install xlsx'
      );
    }

    try {
      const workbook = XLSX.read(fileBuffer, { type: 'array' });
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];

      if (!worksheet) {
        throw new Error('No sheets found in workbook');
      }

      const rows = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
      if (rows.length < 2) {
        throw new Error('Excel file must contain headers and at least one data row');
      }

      const headers = rows[0];
      const mapping = this.autoMapColumns(headers, entityType);

      const entities = [];
      const warnings = [];

      for (let i = 1; i < rows.length; i++) {
        const row = rows[i];
        const entity = {};
        let isEmpty = true;

        for (const [headerName, fieldName] of Object.entries(mapping.mapped)) {
          const colIndex = headers.indexOf(headerName);
          if (colIndex >= 0 && row[colIndex] !== undefined && row[colIndex] !== null) {
            entity[fieldName] = row[colIndex];
            isEmpty = false;
          }
        }

        if (!isEmpty) {
          entities.push(entity);
        }
      }

      return {
        entities,
        unmappedColumns: mapping.unmapped,
        warnings,
      };
    } catch (error) {
      throw new Error(`Failed to import entity data: ${error.message}`);
    }
  }

  /**
   * Import CSV data from string
   * @param {string} csvString
   * @param {string} delimiter
   * @returns {Object} {headers, rows}
   */
  static importCSV(csvString, delimiter = ',') {
    const lines = [];
    let currentLine = '';
    let inQuotes = false;

    for (let i = 0; i < csvString.length; i++) {
      const char = csvString[i];
      const nextChar = csvString[i + 1];

      if (char === '"') {
        inQuotes = !inQuotes;
        currentLine += char;
      } else if (char === '\n' && !inQuotes) {
        lines.push(currentLine);
        currentLine = '';
      } else if (char === '\r' && nextChar === '\n' && !inQuotes) {
        lines.push(currentLine);
        currentLine = '';
        i++; // Skip \n
      } else {
        currentLine += char;
      }
    }

    if (currentLine) {
      lines.push(currentLine);
    }

    const rows = lines.map((line) => this._parseCSVLine(line, delimiter));

    if (rows.length === 0) {
      return { headers: [], rows: [] };
    }

    const headers = rows[0];
    const data = rows.slice(1);

    return { headers, rows: data };
  }

  /**
   * Parse a single CSV line with proper quote handling
   */
  static _parseCSVLine(line, delimiter = ',') {
    const fields = [];
    let currentField = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      const nextChar = line[i + 1];

      if (char === '"') {
        if (inQuotes && nextChar === '"') {
          currentField += '"';
          i++; // Skip next quote
        } else {
          inQuotes = !inQuotes;
        }
      } else if (char === delimiter && !inQuotes) {
        fields.push(currentField.trim());
        currentField = '';
      } else {
        currentField += char;
      }
    }

    fields.push(currentField.trim());
    return fields;
  }

  // ========================================================================
  // EXPORT METHODS
  // ========================================================================

  /**
   * Export project data to Excel buffer
   * @param {Object} projectData
   * @param {Object} options
   * @returns {ArrayBuffer}
   */
  static exportToExcel(projectData, options = {}) {
    if (!XLSX) {
      throw new Error(
        'SheetJS (xlsx) library not installed. Run: npm install xlsx'
      );
    }

    const workbook = XLSX.utils.book_new();
    const sheetOrder = options.sheetOrder || Object.keys(COLUMN_DEFINITIONS);

    for (const entityType of sheetOrder) {
      const entities = projectData[entityType] || [];

      if (entities.length === 0 && !options.includeEmptySheets) {
        continue;
      }

      const columns = COLUMN_DEFINITIONS[entityType] || [];
      const headers = columns.map((col) => col.label);
      const rows = entities.map((entity) => columns.map((col) => entity[col.key]));

      const wsData = [headers, ...rows];
      const worksheet = XLSX.utils.aoa_to_sheet(wsData);

      // Apply header styling if requested
      if (options.headerStyle) {
        const headerCellStyle = {
          fill: { fgColor: { rgb: 'FF1B2A4A' } },
          font: { bold: true, color: { rgb: 'FFFFFFFF' } },
          alignment: { horizontal: 'center', vertical: 'center' },
        };
        for (let i = 0; i < headers.length; i++) {
          const cellRef = XLSX.utils.encode_cell({ r: 0, c: i });
          worksheet[cellRef].s = headerCellStyle;
        }
      }

      XLSX.utils.book_append_sheet(workbook, worksheet, entityType);
    }

    return XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
  }

  /**
   * Export single entity type to Excel
   * @param {Array} entities
   * @param {Array} columns
   * @param {string} sheetName
   * @returns {ArrayBuffer}
   */
  static exportEntityToExcel(entities, columns, sheetName = 'Sheet1') {
    if (!XLSX) {
      throw new Error(
        'SheetJS (xlsx) library not installed. Run: npm install xlsx'
      );
    }

    const headers = columns.map((col) => col.label || col);
    const keys = columns.map((col) => col.key || col);
    const rows = entities.map((entity) =>
      keys.map((key) => entity[key])
    );

    const wsData = [headers, ...rows];
    const worksheet = XLSX.utils.aoa_to_sheet(wsData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

    return XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
  }

  /**
   * Export simulation results to Excel workbook
   * @param {Object} simulationResults
   * @returns {ArrayBuffer}
   */
  static exportResultsToExcel(simulationResults) {
    if (!XLSX) {
      throw new Error(
        'SheetJS (xlsx) library not installed. Run: npm install xlsx'
      );
    }

    const workbook = XLSX.utils.book_new();

    // Summary sheet
    const summaryData = [
      ['Metric', 'Value'],
      ['System Availability', simulationResults.availability || 0],
      ['MTBF (hours)', simulationResults.mtbf || 0],
      ['MTTR (hours)', simulationResults.mttr || 0],
      ['Total Cost', simulationResults.totalCost || 0],
      ['NPV', simulationResults.npv || 0],
      ['IRR', simulationResults.irr || 0],
      ['EAC', simulationResults.eac || 0],
    ];
    const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
    XLSX.utils.book_append_sheet(workbook, summarySheet, 'Summary');

    // Component Results sheet
    if (simulationResults.components) {
      const componentHeaders = [
        'Component ID',
        'Uptime (h)',
        'Downtime (h)',
        'Failures',
        'Repairs',
        'Cost',
      ];
      const componentRows = Object.entries(simulationResults.components).map(
        ([compId, data]) => [
          compId,
          data.uptime || 0,
          data.downtime || 0,
          data.failures || 0,
          data.repairs || 0,
          data.cost || 0,
        ]
      );
      const componentData = [componentHeaders, ...componentRows];
      const componentSheet = XLSX.utils.aoa_to_sheet(componentData);
      XLSX.utils.book_append_sheet(workbook, componentSheet, 'Component Results');
    }

    // Cost Breakdown sheet
    if (simulationResults.costs) {
      const costHeaders = ['Category', 'Amount'];
      const costRows = Object.entries(simulationResults.costs).map(
        ([category, amount]) => [category, amount || 0]
      );
      const costData = [costHeaders, ...costRows];
      const costSheet = XLSX.utils.aoa_to_sheet(costData);
      XLSX.utils.book_append_sheet(workbook, costSheet, 'Cost Breakdown');
    }

    return XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
  }

  /**
   * Export data to CSV string
   * @param {Array} data
   * @param {Array} columns
   * @returns {string}
   */
  static exportToCSV(data, columns) {
    const headers = columns.map((col) => this._escapeCSVField(col));
    const rows = data.map((item) =>
      columns.map((col) => this._escapeCSVField(item[col]))
    );

    return [headers, ...rows].map((row) => row.join(',')).join('\n');
  }

  /**
   * Export simulation results to CSV
   * @param {Object} simulationResults
   * @returns {string}
   */
  static exportResultsToCSV(simulationResults) {
    const lines = [
      'Reliability Analysis Results',
      '',
      'System Metrics',
      'Metric,Value',
      `System Availability,${simulationResults.availability || 0}`,
      `MTBF (hours),${simulationResults.mtbf || 0}`,
      `MTTR (hours),${simulationResults.mttr || 0}`,
      `Total Cost,${simulationResults.totalCost || 0}`,
      '',
      'Component Results',
      'Component ID,Uptime (h),Downtime (h),Failures,Repairs,Cost',
    ];

    if (simulationResults.components) {
      Object.entries(simulationResults.components).forEach(([compId, data]) => {
        lines.push(
          `${compId},${data.uptime || 0},${data.downtime || 0},${data.failures || 0},${data.repairs || 0},${data.cost || 0}`
        );
      });
    }

    return lines.join('\n');
  }

  /**
   * Escape field for CSV output
   */
  static _escapeCSVField(field) {
    if (field === null || field === undefined) {
      return '';
    }

    const str = String(field);

    if (str.includes(',') || str.includes('"') || str.includes('\n')) {
      return `"${str.replace(/"/g, '""')}"`;
    }

    return str;
  }

  // ========================================================================
  // COLUMN MAPPING
  // ========================================================================

  /**
   * Get expected column names for entity type
   */
  static getColumnMapping(entityType) {
    const columns = COLUMN_DEFINITIONS[entityType] || [];
    return columns.map((col) => ({
      key: col.key,
      label: col.label,
      type: col.type,
    }));
  }

  /**
   * Auto-map spreadsheet headers to entity fields
   * Fuzzy matching with case insensitivity and abbrevations
   */
  static autoMapColumns(headers, entityType) {
    const columnDefs = COLUMN_DEFINITIONS[entityType] || [];
    const mapped = {};
    const unmapped = [];

    const normalizeString = (str) => {
      return str.toLowerCase().replace(/[\s_-]/g, '');
    };

    for (const header of headers) {
      const normalizedHeader = normalizeString(header);
      let foundMatch = false;

      for (const colDef of columnDefs) {
        const normalizedLabel = normalizeString(colDef.label);
        const normalizedKey = normalizeString(colDef.key);

        if (normalizedHeader === normalizedLabel || normalizedHeader === normalizedKey) {
          mapped[header] = colDef.key;
          foundMatch = true;
          break;
        }

        // Check for partial matches (fuzzy matching)
        if (
          normalizedHeader.includes(normalizedKey.substring(0, 3)) ||
          normalizedKey.includes(normalizedHeader.substring(0, 3))
        ) {
          mapped[header] = colDef.key;
          foundMatch = true;
          break;
        }
      }

      if (!foundMatch) {
        unmapped.push(header);
      }
    }

    return { mapped, unmapped };
  }

  // ========================================================================
  // VALIDATION
  // ========================================================================

  /**
   * Validate imported data
   * @param {Array} entities
   * @param {string} entityType
   * @returns {Object} {valid, errors, warnings}
   */
  static validateImportData(entities, entityType) {
    const errors = [];
    const warnings = [];
    const seenIds = new Set();

    const columnDefs = COLUMN_DEFINITIONS[entityType] || [];
    const requiredFields = ['id', 'description'];

    for (let i = 0; i < entities.length; i++) {
      const entity = entities[i];

      // Check required fields
      for (const requiredField of requiredFields) {
        if (!entity[requiredField]) {
          errors.push(
            `Row ${i + 1}: Missing required field "${requiredField}"`
          );
        }
      }

      // Check for duplicate IDs
      if (entity.id) {
        if (seenIds.has(entity.id)) {
          errors.push(`Row ${i + 1}: Duplicate ID "${entity.id}"`);
        }
        seenIds.add(entity.id);
      }

      // Validate numeric fields
      for (const colDef of columnDefs) {
        if (colDef.type === 'number' && entity[colDef.key] !== undefined) {
          if (isNaN(Number(entity[colDef.key]))) {
            errors.push(
              `Row ${i + 1}: Invalid numeric value for "${colDef.label}": ${entity[colDef.key]}`
            );
          }
        }
      }

      // Validate reference fields
      if (entity.failureModel && !entity.failureModel.startsWith('FM-')) {
        warnings.push(
          `Row ${i + 1}: Failure mode reference may be invalid: ${entity.failureModel}`
        );
      }
    }

    return {
      valid: errors.length === 0,
      errors,
      warnings,
    };
  }
}
